import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, switchMap } from 'rxjs/operators';
import { MeteringPointService } from '@appStore/metering-points/services/metering-point.service';
import { MeteringPointActionTypes } from '@appStore/metering-points/actions/metering-point.actions';

import * as meteringPointActions from '@appStore/metering-points/actions/metering-point.actions';
import { DatePeriodType, MeteringType } from '@enums';
import { ClimateCompensatedCO2Data } from '@model';

@Injectable()
export class MeteringPointsEffects {
  constructor(private router: Router, private meteringPointService: MeteringPointService, private actions$: Actions) {}

  loadData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.LoadMeteringPointData),
      exhaustMap(action => {
        return this.meteringPointService.getMeteringPointsOnUser().pipe(
          map(meteringPoints => new meteringPointActions.LoadMeteringPointsSuccess(meteringPoints)),
          catchError(error => of(new meteringPointActions.LoadMeteringPointsFail(error)))
        );
      })
    );
  });

  loadDisruptionsData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.LoadMeteringPointsDisruptionsData),
      exhaustMap((action: meteringPointActions.LoadMeteringPointsDisruptions) => {
        return this.meteringPointService.getMeteringPointDisruptionsOnUser(action.payload.fromDate).pipe(
          map(meteringPoints => new meteringPointActions.LoadMeteringPointsDisruptionsSuccess(meteringPoints)),
          catchError(error => of(new meteringPointActions.LoadMeteringPointsDisruptionsFail(error)))
        );
      })
    );
  });
  
  meteringPointFunctionControls$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.LoadMeteringPointFunctionControls),
      map((action: meteringPointActions.LoadMeteringPointFunctionControls) => action.payload),
      exhaustMap((payload: { masterMeteringPointId: number; meteringPointId: number; }) => {
        return this.meteringPointService
        .getMeteringPointFunctionControls(
          payload.masterMeteringPointId,
          payload.meteringPointId
        )
        .pipe(
          map(
            meteringPointsFunctionControls =>
              new meteringPointActions.LoadMeteringPointFunctionControlsSuccess({
                data: meteringPointsFunctionControls, 
                masterMeteringPointId: payload.masterMeteringPointId,
                meteringPointId: payload.meteringPointId
            })
          ),
          catchError(error => 
            of(
              new meteringPointActions.LoadMeteringPointFunctionControlsFail({
                masterMeteringPointId: payload.masterMeteringPointId,
                meteringPointId: payload.meteringPointId
              })))
        );
      })
    );
  });

  meteringPointMeterReadings$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.LoadMeteringPointReadingData),
      map((action: meteringPointActions.LoadMeteringPointReadingData) => action.payload),
      exhaustMap((payload: { masterMeteringPointId: number; meteringPointId: number; resolution: DatePeriodType }) => {
        return this.meteringPointService
          .getMeteringPointReadingsOnMeteringPoint(
            payload.masterMeteringPointId,
            payload.meteringPointId,
            payload.resolution
          )
          .pipe(
            map(
              readings =>
                new meteringPointActions.LoadMeteringPointReadingDataSuccess({
                  masterMeteringPointId: payload.masterMeteringPointId,
                  meteringPointId: payload.meteringPointId,
                  data: readings
                })
            ),
            catchError(error =>
              of(
                new meteringPointActions.LoadMeteringPointReadingDataFail({
                  masterMeteringPointId: payload.masterMeteringPointId,
                  meteringPointId: payload.meteringPointId
                })
              )
            )
          );
      })
    );
  });

  createReportKey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.CreateReportKey),
      switchMap(action => {
        return this.meteringPointService.createReportKey().pipe(
          map(key => new meteringPointActions.CreateReportKeySuccess(key)),
          catchError(error => of(new meteringPointActions.CreateReportKeyFail(error)))
        );
      })
    );
  });
  
  climateCompensatedCO2Data$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(MeteringPointActionTypes.LoadClimateCompensatedCO2Data),
      map((action: meteringPointActions.LoadClimateCompensatedCO2) => action.payload),
      exhaustMap((payload: {
        meteringPointId: number;
        customerId: string;
        from: Date;
        to: Date;
        resolution: string;
        type: string;
        unit: string;
        meteringType: MeteringType;
        compensationKeyFigure: number }) => {
        return this.meteringPointService
          .getClimateCompensatedCO2(
            payload.meteringPointId,
            payload.customerId,
            payload.from,
            payload.to,
            payload.resolution,
            payload.type,
            payload.unit,
            payload.meteringType,
            payload.compensationKeyFigure
          )
          .pipe(
            map(
              result =>
                new meteringPointActions.LoadClimateCompensatedCO2Success(<ClimateCompensatedCO2Data> {
                 meteringPointId: result.meteringPointId,
                 customerId: result.customerId,
                 from: result.from,
                 to: result.to,
                 type: result.type,
                 compensation: result.compensation
                })
            ),
            catchError(error =>
              of(
                new meteringPointActions.LoadClimateCompensatedCO2Fail({
                  meteringPointId: payload.meteringPointId
                })
              )
            )
          );
      })
    );
  });
}
